import React, { Component, ReactNode } from "react";
import { observable, computed, runInAction } from "mobx";
import { observer } from "mobx-react";
import Tooltip, { ITooltipProps } from "../../pieces/Tooltip/Tooltip";
import { AnimationDuration } from "../../../styles/Animations";

export interface ICopyToClipboardProps {
  /**
   * Default props for the tooltip
   */
  tooltip: ITooltipProps;
  /**
   * Tooltip props to show on copy
   */
  tooltipOnCopied: ITooltipProps;
  /**
   * Tooltip props to show on error
   */
  tooltipOnError: ITooltipProps;
  /**
   * Value to copy to the clipboard
   */
  value: string;
  /**
   * Children must be a function to use `onCopy`
   */
  children: (props: ICopyToClipboardRenderProps) => ReactNode;
  /**
   * Only show the tooltip once copied and not on hover
   */
  showTooltipOnCopy?: boolean;
}

interface ICopyToClipboardRenderProps {
  onCopy: () => void;
}

/**
 * A render props component to share the behavior of copying text to the clipboard
 * and giving feedback on the copy button with a tooltip
 */
@observer
class CopyToClipboard extends Component<ICopyToClipboardProps> {
  public static defaultProps = {
    tooltip: { content: "Copy" },
    tooltipOnCopied: { content: "Copied" },
    tooltipOnError: { content: "Could not copy" },
  };

  private waitTimer?: number;
  private hideTimer?: number;

  @observable.ref
  private showTooltip: boolean | undefined = this.props.showTooltipOnCopy ? false : undefined;

  @observable.ref
  private showOnCopiedTooltip = false;

  @observable.ref
  private showOnErrorTooltip = false;

  public componentWillUnmount(): void {
    clearTimeout(this.waitTimer);
    clearTimeout(this.hideTimer);
  }

  @computed
  private get tooltip(): ITooltipProps {
    if (this.showOnErrorTooltip) {
      return this.props.tooltipOnError;
    }

    if (this.showOnCopiedTooltip) {
      return this.props.tooltipOnCopied;
    }

    return this.props.tooltip;
  }

  private copyToClipboard = (): void => {
    try {
      if (navigator?.clipboard) {
        navigator?.clipboard.writeText(this.props.value);
      } else {
        // Fall back to document.execCommand
        const copyElement = document.createElement("textarea");
        copyElement.value = this.props.value;
        document.body.appendChild(copyElement);
        copyElement.select();
        document.execCommand("copy");
        copyElement.remove();
      }
    } catch (error) {
      this.showOnErrorTooltip = true;
    }
  };

  private handleCopy = (): void => {
    clearTimeout(this.waitTimer);
    clearTimeout(this.hideTimer);

    this.showTooltip = true;
    this.showOnCopiedTooltip = true;

    this.copyToClipboard();

    this.waitTimer = window.setTimeout(() => {
      runInAction(() => {
        this.showTooltip = this.props.showTooltipOnCopy ? false : undefined;

        // Wait for the exit transition to finish before changing the tooltip back
        this.hideTimer = window.setTimeout(() => {
          runInAction(() => {
            this.showOnCopiedTooltip = false;
            this.showOnErrorTooltip = false;
          });
        }, AnimationDuration.Medium);
      });
    }, 1200);
  };

  public render(): ReactNode {
    return (
      <Tooltip show={this.showTooltip} {...this.tooltip}>
        {this.props.children({ onCopy: this.handleCopy })}
      </Tooltip>
    );
  }
}

export default CopyToClipboard;
